import React from 'react';
import {Button} from 'antd';
import CapabilityFlow from '../../components/site/CapabilityFlow';
import HeroSection from '../../components/site/HeroSection';
import HowItWorksFlow from '../../components/site/HowItWorksFlow';
import SectionTitle from '../../components/site/SectionTitle';
import SplitShowcase from '../../components/site/SplitShowcase';
import {useWaitlist} from '../../components/site/WaitlistModal';
import {useSiteContent} from './siteContent';
import styles from './SitePages.module.css';

type ProductDirectionKey = 'assist-trading' | 'auto-trading' | 'asset-allocation';

interface ProductDirectionPageProps {
    directionKey: ProductDirectionKey;
}

const directionIndexMap: Record<ProductDirectionKey, number> = {
    'assist-trading': 0,
    'auto-trading': 1,
    'asset-allocation': 2
};

const ProductDirectionPage: React.FC<ProductDirectionPageProps> = ({directionKey}) => {
    const {home, productPage, waitlistCta} = useSiteContent();
    const {openWaitlist} = useWaitlist();
    const directionIndex = directionIndexMap[directionKey];
    const homeDirection = home.products[directionIndex];
    const storyBlock = productPage.directions[directionIndex];

    if (!homeDirection || !storyBlock) {
        return null;
    }

    return (
        <div className={styles.page}>
            <section className={styles.section}>
                <HeroSection
                    description={homeDirection.description}
                    eyebrow={homeDirection.label}
                    metrics={homeDirection.metrics}
                    panelDescription={storyBlock.description}
                    panelLabel={storyBlock.eyebrow}
                    panelImage={directionKey === 'assist-trading' ? '/copoliot.gif' : directionKey === 'auto-trading' ? '/auto_coppliot.gif' : undefined}
                    panelTitle={storyBlock.title}
                    title={homeDirection.slogan}
                />
            </section>

            <section className={styles.section} style={{paddingBottom: 'var(--site-section-gap)'}}>
                <SectionTitle
                    description={storyBlock.featuresTitle?.description ?? homeDirection.description}
                    eyebrow={storyBlock.featuresTitle?.eyebrow ?? homeDirection.label}
                    title={storyBlock.featuresTitle?.title ?? homeDirection.slogan}
                />
                <div className="site-section"/>
                <CapabilityFlow steps={storyBlock.flowSteps} feedbackLoop={directionKey === 'auto-trading'}/>
                <div className="site-section"/>
            </section>

            {storyBlock.usageSteps ? (
                <section className={styles.section}>
                    <SectionTitle
                        description={storyBlock.usageTitle?.description ?? storyBlock.description}
                        eyebrow={storyBlock.usageTitle?.eyebrow ?? storyBlock.eyebrow}
                        title={storyBlock.usageTitle?.title ?? storyBlock.title}
                    />
                    <div className="site-section"/>
                    <HowItWorksFlow steps={storyBlock.usageSteps} columns={3}/>
                </section>
            ) : (
                <section className={styles.section}>
                    <SectionTitle
                        description={storyBlock.description}
                        eyebrow={storyBlock.eyebrow}
                        title={storyBlock.title}
                    />
                    <div className={`${styles.storyStack} site-section`}>
                        <SplitShowcase block={storyBlock}/>
                    </div>
                </section>
            )}

            <section className={styles.section}>
                <div className={styles.ctaPanel}>
                    <div>
                        <span className="site-eyebrow">{home.ctaEyebrow}</span>
                        <h2>{home.ctaTitle}</h2>
                        <p>{home.ctaDescription}</p>
                    </div>
                    <div className={styles.ctaActions}>
                        <Button size="large" type="primary" onClick={openWaitlist}
                                style={{height: 48, paddingInline: 32, fontSize: 16}}>
                            {waitlistCta.label}
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ProductDirectionPage;
